/**
 * @fileoverview Popover genérico de filtro multi-seleção (trigger + popover
 * com título, busca, linha de status com "Aplicar todas"/"Limpar" e lista
 * rolável de pills coloridas com check).
 *
 * É a ÚNICA fonte de layout dos filtros por Etiquetas (TagsFilter) e por
 * Setores (QueuesFilter) do modal de Filtros de /tickets — qualquer ajuste
 * visual feito aqui vale para os dois.
 *
 * `noneOption` é a opção especial ("Sem etiquetas" / "Sem fila"): fica no topo
 * da lista, conta no badge, mas NÃO entra no "Aplicar todas".
 *
 * `placement`: "bottom" (abaixo do trigger, padrão — mobile) ou "right"
 * (desktop: ao lado direito do modal, alinhado ao topo do trigger). Em "right"
 * a posição é medida ao abrir por measureDialogSide — se não couber na
 * viewport, abre abaixo como no mobile. Nunca cobre o modal nem sai da tela.
 */

import React, { useMemo, useState } from "react";
import {
  ButtonBase,
  Popover,
  Typography,
  IconButton,
  Divider,
  InputBase,
  makeStyles,
} from "@material-ui/core";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import { Check, Search, X } from "lucide-react";
import { measureDialogSide } from "../../utils/dialogSidePlacement";

// Largura do paper do popover (ver estilo `popover` abaixo).
const PANEL_WIDTH = 320;

const useStyles = makeStyles((theme) => ({
  // Trigger alinhado VISUAL e DIMENSIONALMENTE ao `<Select variant="outlined">`
  // com `filterDropdownSelect`. O MUI posiciona o fieldset da borda em
  // `position:absolute`, então a borda do MUI não soma na altura do elemento.
  // Aqui a borda faz parte do box (ButtonBase), então compensamos tirando 1.5px
  // do padding vertical em cada lado: 12.5px padding + 1.5px border === 14px
  // padding "puro" do MUI. Resultado: mesma altura total (47px).
  trigger: {
    position: "relative",
    minWidth: 190,
    boxSizing: "border-box",
    borderRadius: 14,
    padding: "12.5px 32px 12.5px 16px",
    justifyContent: "flex-start",
    fontWeight: 500,
    lineHeight: 1.1876,
    border:
      theme.palette.type === "dark"
        ? "1.5px solid rgba(91, 124, 255, 0.3)"
        : "1.5px solid #e2e8f0",
    background:
      theme.palette.type === "dark"
        ? "rgba(39, 39, 42, 0.95)"
        : "#f8fafc",
    transition: "border-color 0.2s ease, box-shadow 0.2s ease",
    "&:hover": {
      borderColor:
        theme.palette.type === "dark"
          ? "rgba(91, 124, 255, 0.7)"
          : theme.palette.primary.main,
    },
  },
  triggerOpen: {
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 0 0 1px rgba(91, 124, 255, 0.15)"
        : "0 0 0 1px rgba(61, 92, 253, 0.1)",
  },
  triggerFullWidth: {
    width: "100%",
  },
  triggerContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.8),
    minWidth: 0,
    flex: 1,
  },
  triggerLabel: {
    display: "block",
    fontSize: "1rem",
    fontWeight: 500,
    lineHeight: 1.1876,
    color: theme.palette.type === "dark" ? "#ffffff" : "#1e293b",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    textAlign: "left",
  },
  // Ícone idêntico ao `.MuiSelect-icon`: posição absoluta à direita,
  // 24px de tamanho, mesmo tom azul e glow do `filterDropdownSelect`.
  triggerIcon: {
    position: "absolute",
    right: 7,
    top: "calc(50% - 12px)",
    width: 24,
    height: 24,
    pointerEvents: "none",
    color: theme.palette.type === "dark" ? "#5B7CFF" : "#64748b",
    filter:
      theme.palette.type === "dark"
        ? "drop-shadow(0 0 4px rgba(91, 124, 255, 0.4))"
        : "none",
  },
  // O trigger usa a mesma cor do texto selecionado mesmo quando é placeholder —
  // casa com o `filterDropdownSelect` dos outros filtros do modal.
  placeholder: {
    color: theme.palette.type === "dark" ? "#ffffff" : "#1e293b",
  },
  countBadge: {
    minWidth: 22,
    height: 22,
    padding: theme.spacing(0, 0.75),
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.8rem",
    fontWeight: 700,
    lineHeight: 1,
    color: "#ffffff",
    background: "#1e80d7",
  },
  popover: {
    "& .MuiPopover-paper": {
      width: 320,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - 48px)",
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(1),
      borderRadius: 20,
      overflow: "hidden",
      background: theme.palette.type === "dark" ? "#22262b" : "#ffffff",
      color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(255, 255, 255, 0.08)"
          : "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow:
        theme.palette.type === "dark"
          ? "0 20px 48px rgba(0, 0, 0, 0.45)"
          : "0 20px 44px rgba(15, 23, 42, 0.16)",
    },
  },
  popoverHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.75, 2, 1),
  },
  popoverTitle: {
    fontSize: "1rem",
    fontWeight: 700,
  },
  popoverClose: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  popoverBody: {
    padding: theme.spacing(0, 1.5, 1.5),
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  searchField: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.7, 1.4),
    borderRadius: 16,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.03)"
        : "rgba(248, 250, 252, 0.9)",
  },
  searchIcon: {
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
  },
  searchInput: {
    flex: 1,
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    "& input::placeholder": {
      color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.55)" : "#6b7280",
      opacity: 1,
    },
  },
  statusRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 0.5, 1.25),
  },
  statusText: {
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.76)" : "#6b7280",
  },
  statusActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  clearButton: {
    border: "none",
    background: "transparent",
    padding: 0,
    cursor: "pointer",
    fontSize: "0.95rem",
    color: theme.palette.primary.main,
  },
  divider: {
    marginBottom: theme.spacing(0.75),
  },
  optionsScrollArea: {
    maxHeight: 320,
    minHeight: 0,
    overflowY: "auto",
    paddingRight: theme.spacing(0.5),
    marginRight: theme.spacing(-0.5),
  },
  option: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.1, 1.4),
    borderRadius: 14,
    marginBottom: theme.spacing(0.75),
    textAlign: "left",
    background: "transparent",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(15, 23, 42, 0.04)",
    },
  },
  optionSelected: {
    background:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.16)"
        : "rgba(59, 130, 246, 0.14)",
    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.2)"
          : "rgba(59, 130, 246, 0.18)",
    },
  },
  optionLabel: {
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
  },
  optionChip: {
    padding: theme.spacing(0.25, 1),
    borderRadius: 999,
    fontSize: "0.86rem",
    lineHeight: 1.4,
    color: "#f8fafc",
    textShadow: "0 1px 1px rgba(0, 0, 0, 0.3)",
  },
  optionCheck: {
    marginLeft: theme.spacing(1.5),
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#7aa2ff" : theme.palette.primary.main,
  },
  emptyState: {
    padding: theme.spacing(1.5, 0.5, 0.5),
    fontSize: "0.94rem",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.6)" : "#6b7280",
    textAlign: "center",
  },
}));

export function FilterPopover({
  options = [],
  selectedIds = [],
  onToggleOption,
  onSelectAll,
  onClear,
  noneOption,
  loading = false,
  placeholder,
  title,
  searchPlaceholder,
  emptyLabel,
  singleSelectedLabel,
  multipleSelectedLabel,
  showingAllLabel,
  selectAllLabel,
  clearLabel,
  loadingLabel,
  fullWidth = false,
  disabled = false,
  className,
  placement = "bottom",
}) {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const open = Boolean(anchorEl);
  // Offset horizontal para abrir ao lado do modal, medido ao abrir; null = abaixo.
  const [sideAnchor, setSideAnchor] = useState(null);

  const filteredOptions = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.name || "").toLowerCase().includes(q));
  }, [options, searchTerm]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedCount = selectedIds.length + (noneOption?.selected ? 1 : 0);

  const handleOpen = (event) => {
    if (disabled) return;
    const trigger = event.currentTarget;
    setSideAnchor(placement === "right" ? measureDialogSide(trigger, PANEL_WIDTH) : null);
    setAnchorEl(trigger);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchTerm("");
  };

  // "Aplicar todas": seleciona de uma vez todas as opções visíveis (com busca
  // ativa, só as filtradas), somando às já marcadas. Só aparece se houver
  // alguma visível ainda não marcada.
  const canSelectAll = filteredOptions.some((o) => !selectedSet.has(o.id));

  const handleSelectAll = () => {
    const merged = new Set([...selectedIds, ...filteredOptions.map((o) => o.id)]);
    onSelectAll(Array.from(merged));
  };

  const statusLabel = useMemo(() => {
    if (selectedCount === 0) return showingAllLabel;
    const suffix =
      selectedCount === 1 ? singleSelectedLabel : multipleSelectedLabel;
    return `${selectedCount} ${suffix}`;
  }, [selectedCount, singleSelectedLabel, multipleSelectedLabel, showingAllLabel]);

  return (
    <>
      <ButtonBase
        className={[
          classes.trigger,
          fullWidth ? classes.triggerFullWidth : "",
          open ? classes.triggerOpen : "",
          className || "",
        ]
          .filter(Boolean)
          .join(" ")}
        onClick={handleOpen}
        disabled={disabled}
      >
        <div className={classes.triggerContent}>
          <Typography
            className={`${classes.triggerLabel} ${
              selectedCount === 0 ? classes.placeholder : ""
            }`}
          >
            {placeholder}
          </Typography>
          {selectedCount > 0 && (
            <span className={classes.countBadge}>{selectedCount}</span>
          )}
        </div>
        <ArrowDropDownIcon className={classes.triggerIcon} />
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={sideAnchor != null ? { vertical: "top", horizontal: sideAnchor } : { vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        className={classes.popover}
        PaperProps={sideAnchor != null ? { style: { marginTop: 0 } } : undefined}
      >
        <div className={classes.popoverHeader}>
          <Typography className={classes.popoverTitle}>{title}</Typography>
          <IconButton
            size="small"
            className={classes.popoverClose}
            onClick={handleClose}
          >
            <X size={18} />
          </IconButton>
        </div>

        <div className={classes.popoverBody}>
          <div className={classes.searchField}>
            <Search className={classes.searchIcon} />
            <InputBase
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder}
              className={classes.searchInput}
              autoFocus
            />
          </div>

          <div className={classes.statusRow}>
            <Typography className={classes.statusText}>{statusLabel}</Typography>
            <div className={classes.statusActions}>
              {selectAllLabel && !loading && canSelectAll && (
                <button
                  type="button"
                  className={classes.clearButton}
                  onClick={handleSelectAll}
                >
                  {selectAllLabel}
                </button>
              )}
              {selectedCount > 0 && clearLabel && (
                <button
                  type="button"
                  className={classes.clearButton}
                  onClick={onClear}
                >
                  {clearLabel}
                </button>
              )}
            </div>
          </div>

          <Divider className={classes.divider} />

          <div className={classes.optionsScrollArea}>
            {noneOption?.label && (
              <ButtonBase
                className={`${classes.option} ${
                  noneOption.selected ? classes.optionSelected : ""
                }`}
                onClick={noneOption.onToggle}
              >
                <Typography className={classes.optionLabel}>
                  {noneOption.label}
                </Typography>
                {noneOption.selected && <Check className={classes.optionCheck} />}
              </ButtonBase>
            )}

            {loading ? (
              <Typography className={classes.emptyState}>
                {loadingLabel}
              </Typography>
            ) : filteredOptions.length === 0 ? (
              <Typography className={classes.emptyState}>
                {emptyLabel}
              </Typography>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedSet.has(option.id);
                return (
                  <ButtonBase
                    key={option.id}
                    className={`${classes.option} ${
                      isSelected ? classes.optionSelected : ""
                    }`}
                    onClick={() => onToggleOption(option.id)}
                  >
                    <span
                      className={classes.optionChip}
                      style={{ backgroundColor: option.color || "#4b5563" }}
                    >
                      {option.name}
                    </span>
                    {isSelected && (
                      <Check className={classes.optionCheck} />
                    )}
                  </ButtonBase>
                );
              })
            )}
          </div>
        </div>
      </Popover>
    </>
  );
}
